import {
  AHText,
  AHTextFonts,
  Box,
  ScaleFactorProvider,
  SignedActor,
  useLayoutContext,
} from "arkh";

import { graphql, useFragment } from "react-relay";
import { FormattedMessage, useIntl } from "react-intl";
import { NoteEditSharedInfo_widgetNote$key } from "./__generated__/NoteEditSharedInfo_widgetNote.graphql";
import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { NoteEditInfo_page$key } from "./__generated__/NoteEditInfo_page.graphql";

import { LayoutChangeEvent, Platform, TextInput, View } from "react-native";
import { useBackdrop } from "../WidgetSettings/useBackdrop";
import { NoteEdit$key } from "./__generated__/NoteEdit.graphql";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  SharedValue,
} from "react-native-reanimated";
import { useWidgetMargin } from "../Widget/useWidgetMargin";
import { useOpenImagePicker } from "./openImagePicker";
import { useSaveNoteImage } from "./useSaveNoteImage";
import { useNoteRemovePage } from "./useNoteRemovePage";
import { useAlert } from "../../../../common/Alert";
import { AnimatedBackdrop } from "../common/AnimatedBackdrop";
import { WidgetLayoutProvider } from "widgets";
import { DropFileZone, droppedFileToImage } from "../../../Files/DropFileZone";
import { useWidgetLayout } from "../Widget/useWidgetLayout";
import { WidgetNoteTextMode } from "./WidgetNoteTextModePage";
import { WidgetNoteCameraMode } from "./WidgetNoteCameraMode";
import { WidgetNoteImageMode } from "./WidgetNotePageImageMode";
import { WidgetNoteTextModeAccessoryView } from "./WidgetNoteTextModeAccessoryView";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FormattedDate } from "../../../../common/dates/FormattedDate";

export function NoteEdit(props: {
  uploadImage(
    localUri: string,
    { width, height }: { width: number; height: number }
  ): Promise<{
    localImageURI: string;
    imageId: string;
  }>;
  pageId: string;
  onClosed(): void;
  onStart(): void;
  unitSize: number;
  note: NoteEdit$key;
  startLeft: SharedValue<number> | number;
  startTop: number;
  targetTop?: number;
}) {
  "use no memo";
  const [screenSize, setScreenSize] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width > 0) {
      setScreenSize({
        width: event.nativeEvent.layout.width,
        height: event.nativeEvent.layout.height,
      });
    }
  }, []);

  const note = useFragment(
    graphql`
      fragment NoteEdit on Note {
        id
        ...NoteEditSharedInfo_widgetNote
        pages {
          id
          ...WidgetNoteCameraMode_widgetNotePage
          ...WidgetNotePageImageMode_widgetNotePage

          ...NoteEditInfo_page
          ...WidgetNoteTextModePage_widgetNotePage
          ...WidgetNoteTextModeAccessoryView_page
        }
        noteSize
      }
    `,
    props.note
  );

  const margin = useWidgetMargin();
  const w = 2;
  const h = note.noteSize === "TWO_X_ONE" ? 1 : 2;

  const width = useMemo(
    () => props.unitSize * w - margin * 2,
    [margin, props.unitSize, w]
  );
  const height = useMemo(
    () => props.unitSize * h - margin * 2,
    [margin, props.unitSize, h]
  );

  const insets = useSafeAreaInsets();

  const layout = useLayoutContext();
  const textModeTargetTop = useMemo(() => {
    if (Platform.OS === "web") {
      return screenSize.height / 2 - height / 1.5;
    }
    return props.targetTop ?? Math.max(insets.top, margin);
  }, [screenSize.height, height, insets.top, margin, props.targetTop]);
  const textModeTargetLeft = useMemo(() => {
    return screenSize.width > 0 ? screenSize.width / 2 - width / 2 : 0;
  }, [width, screenSize.width]);

  const intl = useIntl();

  const { animation, ...backdrop } = useBackdrop(undefined, props.onStart);
  const textInputRef = useRef<TextInput>(null);
  const [mode, setMode] = useState<
    | { type: "TEXT" }
    | { type: "CAMERA" }
    | {
        type: "IMAGE";
        image: {
          uri: string;
          width: number;
          height: number;
          imageId?: string;
        };
      }
  >({ type: "TEXT" });

  const stLeft =
    typeof props.startLeft === "number"
      ? // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/rules-of-hooks
        (useDerivedValue(() => props.startLeft) as SharedValue<number>)
      : props.startLeft;

  const translateX = useDerivedValue(() => {
    return interpolate(
      animation.value,
      [0, 1],
      [stLeft.value - textModeTargetLeft, 0]
    );
  });

  const animatedWidgetContainerStyle = useAnimatedStyle(() => {
    return {
      zIndex: 1,
      position: "absolute",
      top: textModeTargetTop,
      left: textModeTargetLeft,
      width,
      height,
      transform: [
        {
          translateY: interpolate(
            animation.value,
            [0, 1],
            [props.startTop - textModeTargetTop, 0]
          ),
        },
        {
          translateX: translateX.value,
        },
      ],
    };
  });

  const onDone = useCallback(() => {
    textInputRef.current?.blur();
    backdrop.close(props.onClosed);
  }, [backdrop, props.onClosed]);

  const page = useMemo(
    () => note.pages.find(({ id }) => id === props.pageId),
    [note.pages, props.pageId]
  );
  const openImagePicker = useOpenImagePicker();
  const uploadImage = props.uploadImage;
  const onOpenImagePicker = useCallback(async () => {
    const image = await openImagePicker();
    if (image) {
      uploadImage(image.uri, { width: image.width, height: image.height }).then(
        ({ localImageURI, imageId }) => {
          setMode((m) =>
            m.type === "IMAGE" && m.image.uri === localImageURI
              ? { ...m, image: { ...m.image, imageId } }
              : m
          );
        }
      );

      if (image.height && image.width) {
        setMode({ type: "IMAGE", image: image as any });
      }
    }
  }, [openImagePicker, uploadImage]);

  const [_onSaveImage] = useSaveNoteImage(note.id);
  const onSaveImage = useCallback(
    (image: {
      uri: string;
      translateX: number;
      translateY: number;
      scale: number;
      width: number;
      height: number;
      id: string;
    }) => {
      _onSaveImage(props.pageId, image.uri, image.id, {
        translateX: image.translateX,
        translateY: image.translateY,
        scale: image.scale,
        width: image.width,
        height: image.height,
      });
      setMode({ type: "TEXT" });
    },
    [_onSaveImage, props.pageId]
  );

  const [removePage] = useNoteRemovePage(note.id, props.pageId);

  const Alert = useAlert();
  const onRemovePage = useCallback(() => {
    Alert.alert(
      intl.formatMessage({
        defaultMessage: "Remove page",
        description: "WidgetNoteFocus:RemovePageAlert:title",
      }),
      intl.formatMessage({
        defaultMessage: "Do you want to remove this page",
        description: "WidgetNoteFocus:RemovePageAlert:title",
      }),
      [
        {
          onPress: () => {
            removePage();
            onDone();
          },
          text: intl.formatMessage({
            defaultMessage: "Remove page",
            description: "WidgetNoteFocus:RemovePageAlert:removeoption:text",
          }),
          style: "destructive",
        },
        {
          onPress: () => {},
          text: intl.formatMessage({
            defaultMessage: "Cancel",
            description: "WidgetNoteFocus:RemovePageAlert:canceloption:text",
          }),
          style: "cancel",
        },
      ]
    );
  }, [Alert, intl, onDone, removePage]);

  const onCancel = useCallback(() => setMode({ type: "TEXT" }), []);

  const widgetLayout = useWidgetLayout(props.unitSize, {
    w,
    h,
  });

  return (
    <>
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onLayout={onLayout}
      >
        {screenSize.width ? (
          <>
            {mode.type === "TEXT" ? (
              <AnimatedBackdrop animation={animation} onPress={onDone} />
            ) : null}

            <ScaleFactorProvider value={props.unitSize / 236}>
              <Animated.View style={animatedWidgetContainerStyle}>
                {mode.type === "TEXT" && page ? (
                  <>
                    <WidgetLayoutProvider value={widgetLayout}>
                      <DropFileZone
                        type="IMAGE"
                        maxSize={20000000}
                        disableForeground
                        onDropFilesNew={async (files) => {
                          await Promise.all(
                            files
                              .slice(0, 1)
                              .map(async ({ file, abstractType }) => {
                                if (abstractType === "IMAGE") {
                                  const { width, height, uri } =
                                    await droppedFileToImage(file);
                                  setMode({
                                    type: "IMAGE",
                                    image: {
                                      uri,
                                      width,
                                      height,
                                      imageId: undefined,
                                    },
                                  });

                                  await uploadImage(uri, {
                                    width,
                                    height,
                                  }).then(({ localImageURI, imageId }) => {
                                    setMode((m) =>
                                      m.type === "IMAGE" &&
                                      m.image.uri === localImageURI
                                        ? {
                                            ...m,
                                            image: { ...m.image, imageId },
                                          }
                                        : m
                                    );
                                  });
                                }
                              })
                          );
                        }}
                      >
                        <WidgetNoteTextMode
                          widgetId={note.id}
                          page={page}
                          ref={textInputRef}
                        />
                      </DropFileZone>
                    </WidgetLayoutProvider>
                    <Suspense fallback={null}>
                      <ScaleFactorProvider value={1}>
                        <Box
                          spaceTop="small"
                          style={{
                            alignSelf: "flex-start",
                            position: "absolute",
                            bottom: -layout.space.large - layout.space.small,
                            flexDirection: "column",
                          }}
                        >
                          <EditInfo page={page} />
                          <EditSharedWidgetInfo note={note} />
                        </Box>
                      </ScaleFactorProvider>
                    </Suspense>
                  </>
                ) : null}
              </Animated.View>
            </ScaleFactorProvider>

            <Suspense fallback={null}>
              {mode.type === "CAMERA" && page ? (
                <WidgetNoteCameraMode
                  uploadImage={uploadImage}
                  page={page}
                  animation={animation}
                  onSave={onSaveImage}
                  widgetLayout={{
                    height,
                    width,
                    size: note.noteSize,
                  }}
                  onCancel={onCancel}
                />
              ) : null}
              {mode.type === "IMAGE" && page ? (
                <WidgetNoteImageMode
                  unitSize={props.unitSize}
                  image={mode.image}
                  page={page}
                  noteId={note.id}
                  widgetLayout={widgetLayout}
                  onCancel={onCancel}
                  onSave={onSaveImage}
                />
              ) : null}
            </Suspense>
          </>
        ) : null}
      </View>

      {mode.type === "TEXT" && page ? (
        <ScaleFactorProvider value={1}>
          <WidgetNoteTextModeAccessoryView
            animation={animation}
            widgetLayout={{
              top: textModeTargetTop,
              left: textModeTargetLeft,
              width,
              height,
            }}
            openCamera={() => {
              setMode({ type: "CAMERA" });
            }}
            openImagePicker={onOpenImagePicker}
            widgetId={note.id}
            page={page}
            onDone={onDone}
            onRemovePage={onRemovePage}
          />
        </ScaleFactorProvider>
      ) : null}
    </>
  );
}

function EditInfo(props: { page: NoteEditInfo_page$key }) {
  const page = useFragment(
    graphql`
      fragment NoteEditInfo_page on WidgetNotePage {
        updatedAtDate {
          ...FormattedDate
        }

        updatedByActor {
          ...SignedActor_actor
        }
      }
    `,
    props.page
  );

  if (page.updatedAtDate === null) {
    return null;
  }

  return (
    <Box
      style={{
        alignItems: "center",
        alignContent: "center",

        flexDirection: "row",
        flexGrow: 0,
        flexShrink: 1,
      }}
      spaceBottom="xxsmall"
    >
      {page.updatedByActor ? (
        <Box spaceRight="small">
          <SignedActor signedActor={page.updatedByActor} colorMode="inverted" />
        </Box>
      ) : null}
      <AHText font={AHTextFonts.BOLD} type="detail">
        {page.updatedAtDate ? (
          <FormattedDate date={page.updatedAtDate} />
        ) : null}
      </AHText>
    </Box>
  );
}

function EditSharedWidgetInfo(props: {
  note: NoteEditSharedInfo_widgetNote$key;
}) {
  const note = useFragment(
    graphql`
      fragment NoteEditSharedInfo_widgetNote on Note {
        widgets {
          storecast {
            id
            name
          }
        }
      }
    `,
    props.note
  );
  const uniqueStorecasts = useMemo(() => {
    return (
      note?.widgets
        ?.filter((s) => s.storecast)
        .filter(function onlyUnique(value, index, array) {
          return (
            array.findIndex((a) => a.storecast?.id === value.storecast?.id) ===
            index
          );
        }) ?? []
    );
  }, [note.widgets]);
  if (uniqueStorecasts.length < 2) {
    return null;
  }

  if (uniqueStorecasts.length > 3) {
    return (
      <AHText type="detail">
        <FormattedMessage
          values={{ count: uniqueStorecasts.length }}
          defaultMessage="Updates on {count} StoreCasts"
          description="WidgetNoteEdit updates on multiple storecasts"
        />
      </AHText>
    );
  }

  return (
    <AHText type="detail">
      <FormattedMessage
        values={{
          storecasts: uniqueStorecasts.map((s) => s.storecast?.name).join(", "),
        }}
        defaultMessage="Updates on: {storecasts}"
        description="WidgetNoteEdit updates on multiple storecasts"
      />
    </AHText>
  );
}
